<template>
  <div class="notify-container">
    <v-btn
      :dark="!disabled"
      color="primary"
      :disabled="disabled"
      :ripple="false"
      text
      class="btn-link"
      @click="open"
    >
      เตือนฉันเมื่อพร้อมจำหน่าย
      <v-icon>
        mdi-chevron-right
      </v-icon>
    </v-btn>

    <v-dialog v-model="dialog" max-width="720">
      <v-card class="modal-container">
        <div class="header-modal">
          <div class="header-modal-title">
            สินค้าที่คุณเลือกไม่พร้อมจำหน่ายตอนนี้
          </div>
        </div>

        <button class="btn-close" @click="close">
          <v-icon class="icon-close">
            mdi-close
          </v-icon>
        </button>

        <div class="body-modal">
          <v-row v-bind="{ justify: 'end' }">
            <v-col v-if="data" cols="12" class="text-lg font-bold">
              {{ data?.name || '' }}
            </v-col>

            <v-col cols="12">
              <span class="text-md">
                กรุณากรอกข้อมูล
                เพื่อรับการแจ้งเตือนให้คุณไม่พลาดเมื่อสินค้านี้พร้อมจำหน่ายอีกครั้ง!
              </span>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.email"
                color="primary"
                :error="$v.form.email.$error"
                :error-messages="
                  $v.form.email.$error && handleErrorMessages('email')
                "
                :hide-details="!$v.form.email.$error"
                label="อีเมล"
                large
                outlined
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.phone"
                color="primary"
                :error="$v.form.phone.$error"
                :error-messages="
                  $v.form.phone.$error && handleErrorMessages('phone')
                "
                :hide-details="!$v.form.phone.$error"
                maxlength="10"
                minlength="10"
                oninput="this.value=this.value.slice(0,this.maxLength).replace(/[^\d]+/g, '')"
                label="เบอร์ติดต่อ"
                large
                outlined
              ></v-text-field>
            </v-col>

            <v-col cols="12">
              <v-checkbox
                v-model="form.consent"
                :error="$v.form.consent.$error"
                color="primary"
                :error-messages="
                  $v.form.consent.$error &&
                    handleErrorMessages('consent')
                "
                :hide-details="!$v.form.consent.$error"
                required
                class="mt-0"
              >
                <template v-slot:label>
                  <span>
                    ฉันรับทราบว่าฉันได้อ่านและยอมรับ
                    <a
                      :href="termOfSaleUrl"
                      class="link"
                      target="_blank"
                      @click.stop
                    >
                      เงื่อนไขการส่งเสริมการขาย
                    </a>
                    และ
                    <a
                      :href="privacyPolicyUrl"
                      class="link"
                      target="_blank"
                      @click.stop
                    >
                      นโยบายความเป็นส่วนตัว
                    </a>
                    ของ Com7 เรียบร้อยแล้ว
                  </span>
                </template>
              </v-checkbox>
            </v-col>

            <v-col cols="12" class="modal-actions-container">
              <v-btn depressed link large @click="close">
                ยกเลิก
              </v-btn>
              <v-btn
                depressed
                large
                class="font-bold"
                color="primary"
                :loading="isLoading"
                @click="handleSubmit"
              >
                แจ้งเตือนฉัน
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { validationMixin } from 'vuelidate'
import { required, email } from 'vuelidate/lib/validators'
import isBnn from '@/config/isBnn'

export default {
  name: 'BaseReminder',

  mixins: [validationMixin],

  props: {
    title: {
      type: String,
      default: 'ยืนยันตัวตน',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
      default: () => ({}),
    },
  },

  components: {},

  computed: {
    ...mapState({
      bu: state => state.campaign.current.data.type,
    }),

    isLoading() {
      return (
        this.$store.getters['reminder/isLoading']('submit') ?? false
      )
    },

    termOfSaleUrl() {
      if (isBnn) {
        return 'https://www.bnn.in.th/th/pages/terms-of-sale'
      }
      return 'https://www.studio7thailand.com/th/pages/terms-of-sale'
    },

    privacyPolicyUrl() {
      if (isBnn) {
        return 'https://www.bnn.in.th/th/pages/privacy-policy'
      }
      return 'https://www.studio7thailand.com/th/pages/privacy-policy'
    },
  },

  data() {
    return {
      dialog: false,
      form: {
        email: '',
        phone: '',
        bu: '',
        sku: '',
        consent: false,
      },
    }
  },

  validations: {
    form: {
      email: {
        required,
        email,
      },
      phone: {
        required,
        valid: value => /^(0[2689]{1}[0-9]{8})$/.test(value),
      },
      consent: {
        required,
        valid: value => {
          return value === true
        },
      },
    },
  },

  methods: {
    open() {
      this.dialog = true
    },

    close() {
      this.dialog = false
      this.handleResetForm()
    },

    handleErrorMessages(field) {
      const errors = []

      !this.$v.form[field].required && errors.push('กรุณากรอกข้อมูล')

      if (field === 'email') {
        !this.$v.form[field].email &&
          this.$v.form[field].$invalid &&
          errors.push('กรุณากรอกอีเมลให้ถูกต้อง')
      }

      if (field === 'phone') {
        !this.$v.form[field].valid &&
          this.$v.form[field].$invalid &&
          errors.push('กรุณากรอกเบอร์ติดต่อให้ถูกต้อง')
      }

      if (field === 'consent') {
        !this.$v.form[field].valid &&
          this.$v.form[field].$invalid &&
          errors.push('กรุณายอมรับเงื่อนไข')
      }

      return errors
    },

    handleSubmit() {
      this.$v.$touch()

      if (this.$v.$invalid) {
        return
      }

      //  For ensure sku and bu are set
      this.form = {
        ...this.form,
        bu: this.bu,
        sku: this.data?.sku || '',
      }

      this.$emit('submit', this.form)
    },

    handleResetForm() {
      this.form = {
        email: '',
        phone: '',
        bu: this.bu,
        sku: '',
        consent: false,
      }

      this.$v.$reset()
    },
  },

  beforeDestroy() {
    this.$v.$reset()
  },
}
</script>

<style lang="stylus" scoped>
.notify-container
    height: fit-content

.modal-container
  position: relative
  padding: 24px

.header-modal
  display: flex
  width: 100%
  margin-bottom: 12px

.header-modal-title
  font-size: 32px
  font-weight: bold
  font-family: $font-family-base !important
  align-self: center
  padding-right: 40px

.btn-close {
    position absolute;
    top: 24px
    right: 24px

    width: 40px;
    height: 40px;

    background: #e8e8e8;
    border-radius: 50% !important;

    z-index 10

    &:hover {
        .icon-close {
            color: rgba(0,0,0,0.72)
        }
    }

    &:focus {
        outline: .125rem solid #0071e3;
        outline-offset: .125rem;
        outline-offset: .125rem;
    }

    .icon-close {
        color: rgba(18,18,18,0.56)
    }
}

.modal-actions-container
  display: flex
  justify-content: flex-end
  align-items: center
  gap: 24px
  padding-bottom: 0
  @media small {
    flex-direction: column
    gap: 16px

    > .v-btn {
      width: 100%
    }
  }

.link
  width: fit-content
  display: inline
  color: $color-bnn
  text-decoration: underline
  z-index 20
</style>
